import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Stack, Divider, Chip, Alert, List, ListItemButton, ListItemText, IconButton, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageHeader from '../../../components/compliance/layout/PageHeader';

import { apiDelete, apiGet, apiPost } from '../../../compliance_api/restClient';
import { useAuth } from '../../../contexts/AuthContext';
function normalizeCitation(c) {
  return {
    documentId: c.document_id ?? c.documentId,
    chunkId: c.chunk_id ?? c.chunkId,
    clauseId: c.clause_id ?? c.clauseId,
    pageNumber: c.page_number ?? c.pageNumber,
    jurisdiction: c.jurisdiction,
    versionLabel: c.version_label ?? c.versionLabel,
    effectiveFrom: c.effective_from ?? c.effectiveFrom,
    snippetText: c.snippet_text ?? c.snippetText
  };
}
function formatRelativeTime(iso) {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}
function CitationsBlock({
  citations
}) {
  if (!citations.length) return null;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      mt: 1.5
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    sx: {
      mb: 1,
      fontWeight: 700
    }
  }, "Citations"), /*#__PURE__*/React.createElement(Stack, {
    spacing: 1
  }, citations.map((c, i) => /*#__PURE__*/React.createElement(Box, {
    key: i,
    sx: {
      p: 1.5,
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'action.hover'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, c.jurisdiction, " \u2022 ", c.versionLabel, " \u2022 Clause ", c.clauseId || '(unlabelled)', " \u2022 Page", ' ', c.pageNumber ?? '—'), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      display: 'block',
      mt: 0.5
    }
  }, c.snippetText)))));
}
export default function ChatPage() {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const activeTitle = useMemo(() => conversations.find(c => c.id === activeId)?.title || 'New chat', [conversations, activeId]);
  const loadConversations = useCallback(async () => {
    if (!token) return;
    setLoadingList(true);
    try {
      const data = await apiGet('/api/v1/chat/conversations/', token);
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load chat history');
    } finally {
      setLoadingList(false);
    }
  }, [token]);
  const loadConversation = useCallback(async id => {
    if (!token) return;
    setLoadingThread(true);
    setError(null);
    try {
      const data = await apiGet(`/api/v1/chat/conversations/${id}/`, token);
      const raw = data.messages || [];
      setMessages(raw.map(m => ({
        id: String(m.id),
        role: m.role,
        text: String(m.text),
        confidence: m.confidence,
        citations: Array.isArray(m.citations) ? m.citations.map(normalizeCitation) : [],
        createdAt: m.created_at
      })));
      setActiveId(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load conversation');
    } finally {
      setLoadingThread(false);
    }
  }, [token]);
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);
  const startNewChat = async () => {
    setError(null);
    try {
      const conv = await apiPost('/api/v1/chat/conversations/', {}, token);
      await loadConversations();
      setActiveId(conv.id);
      setMessages([]);
      setText('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start new chat');
    }
  };
  const requestDeleteChat = (conv, e) => {
    e.stopPropagation();
    setDeleteTarget(conv);
  };
  const confirmDeleteChat = async () => {
    if (!token || !deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await apiDelete(`/api/v1/chat/conversations/${deleteTarget.id}/`, token);
      if (activeId === deleteTarget.id) {
        setActiveId(null);
        setMessages([]);
      }
      setDeleteTarget(null);
      await loadConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete chat');
    } finally {
      setDeleting(false);
    }
  };
  const onSend = async () => {
    const q = text.trim();
    if (!q || sending) return;
    setSending(true);
    setError(null);
    let conversationId = activeId;
    try {
      if (!conversationId) {
        const conv = await apiPost('/api/v1/chat/conversations/', {}, token);
        conversationId = conv.id;
        setActiveId(conversationId);
      }
      const userMsg = {
        id: `temp-user-${Date.now()}`,
        role: 'user',
        text: q
      };
      setMessages(m => [...m, userMsg]);
      setText('');
      const res = await apiPost(`/api/v1/chat/conversations/${conversationId}/messages/`, {
        text: q
      }, token);
      const assistantMsg = {
        id: res.response_id || `temp-assistant-${Date.now()}`,
        role: 'assistant',
        text: res.answer || '',
        confidence: res.confidence,
        citations: Array.isArray(res.citations) ? res.citations.map(c => normalizeCitation(c)) : []
      };
      setMessages(m => [...m, assistantMsg]);
      await loadConversations();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send message');
      setMessages(m => m.filter(msg => !msg.id.startsWith('temp-user-')));
      setText(q);
    } finally {
      setSending(false);
    }
  };
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 3
  }, /*#__PURE__*/React.createElement(PageHeader, {
    title: "AI Code Librarian",
    subtitle: "Ask compliance questions. Your conversations are saved automatically."
  }), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        md: '280px 1fr'
      },
      gap: 2,
      minHeight: {
        md: 560
      }
    }
  }, /*#__PURE__*/React.createElement(Card, {
    variant: "outlined",
    sx: {
      borderRadius: 3,
      display: {
        xs: activeId ? 'none' : 'block',
        md: 'block'
      }
    }
  }, /*#__PURE__*/React.createElement(CardContent, {
    sx: {
      p: 2
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    justifyContent: "space-between",
    alignItems: "center",
    sx: {
      mb: 1
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    fontWeight: 700
  }, "History"), /*#__PURE__*/React.createElement(Tooltip, {
    title: "New chat"
  }, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    color: "primary",
    onClick: startNewChat
  }, /*#__PURE__*/React.createElement(AddIcon, null)))), loadingList ? /*#__PURE__*/React.createElement(Box, {
    sx: {
      py: 4,
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: 28
  })) : conversations.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      py: 2
    }
  }, "No chats yet. Start a new conversation.") : /*#__PURE__*/React.createElement(List, {
    dense: true,
    disablePadding: true,
    sx: {
      maxHeight: 480,
      overflow: 'auto'
    }
  }, conversations.map(c => /*#__PURE__*/React.createElement(ListItemButton, {
    key: c.id,
    selected: c.id === activeId,
    onClick: () => loadConversation(c.id),
    sx: {
      borderRadius: 2,
      mb: 0.5
    }
  }, /*#__PURE__*/React.createElement(ListItemText, {
    primary: c.title,
    secondary: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      component: "span",
      variant: "caption",
      noWrap: true,
      display: "block"
    }, c.preview || 'No messages'), /*#__PURE__*/React.createElement(Typography, {
      component: "span",
      variant: "caption",
      color: "text.secondary"
    }, formatRelativeTime(c.updated_at))),
    primaryTypographyProps: {
      noWrap: true,
      fontWeight: c.id === activeId ? 700 : 500
    }
  }), /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    edge: "end",
    "aria-label": "Delete chat",
    onClick: e => requestDeleteChat(c, e)
  }, /*#__PURE__*/React.createElement(DeleteOutlineIcon, {
    fontSize: "small"
  }))))))), /*#__PURE__*/React.createElement(Card, {
    variant: "outlined",
    sx: {
      borderRadius: 3
    }
  }, /*#__PURE__*/React.createElement(CardContent, null, /*#__PURE__*/React.createElement(Stack, {
    spacing: 2
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    alignItems: "center",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    fontWeight: 700
  }, activeTitle), activeId && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    sx: {
      display: {
        md: 'none'
      }
    },
    onClick: () => setActiveId(null)
  }, "Back to history")), error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, error) : null, /*#__PURE__*/React.createElement(Box, {
    sx: {
      minHeight: 360,
      maxHeight: 480,
      overflow: 'auto',
      p: 2,
      border: 1,
      borderColor: 'divider',
      borderRadius: 2
    }
  }, loadingThread ? /*#__PURE__*/React.createElement(Box, {
    sx: {
      py: 6,
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, null)) : messages.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    color: "text.secondary",
    sx: {
      py: 4,
      textAlign: 'center'
    }
  }, "Ask a compliance question to get started. Previous messages appear here after you send.") : /*#__PURE__*/React.createElement(Stack, {
    spacing: 2
  }, messages.map(m => /*#__PURE__*/React.createElement(Box, {
    key: m.id
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    alignItems: "center",
    spacing: 1,
    sx: {
      mb: 0.5
    }
  }, /*#__PURE__*/React.createElement(Chip, {
    label: m.role === 'assistant' ? 'Assistant' : 'You',
    size: "small",
    color: m.role === 'assistant' ? 'primary' : 'default',
    variant: m.role === 'assistant' ? 'filled' : 'outlined'
  }), m.role === 'assistant' && m.confidence != null ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, Math.round(m.confidence * 100), "% confidence") : null), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      whiteSpace: 'pre-wrap'
    }
  }, m.text), m.role === 'assistant' && m.citations?.length ? /*#__PURE__*/React.createElement(CitationsBlock, {
    citations: m.citations
  }) : null, /*#__PURE__*/React.createElement(Divider, {
    sx: {
      mt: 2
    }
  }))), sending ? /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 1,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: 18
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "Thinking\u2026")) : null)), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 1
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    placeholder: "Ask a compliance question\u2026",
    value: text,
    onChange: e => setText(e.target.value),
    onKeyDown: e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    },
    multiline: true,
    maxRows: 4,
    disabled: sending
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: sending || !text.trim(),
    onClick: onSend
  }, "Send")))))), /*#__PURE__*/React.createElement(Dialog, {
    open: Boolean(deleteTarget),
    onClose: () => !deleting && setDeleteTarget(null),
    maxWidth: "xs",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(DialogTitle, null, "Delete conversation?"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(DialogContentText, null, deleteTarget ? `This will permanently delete "${deleteTarget.title}" and all messages in it. This cannot be undone.` : '')), /*#__PURE__*/React.createElement(DialogActions, {
    sx: {
      px: 3,
      pb: 2
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDeleteTarget(null),
    disabled: deleting
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    color: "error",
    onClick: confirmDeleteChat,
    disabled: deleting
  }, deleting ? 'Deleting…' : 'Delete'))));
}